import { ScrollArea } from "@radix-ui/react-scroll-area";
import { User } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { CustomerDetailFragment } from "@/gql/graphql";
import { useCustomerEdit } from "../../hooks/use-customer-edit";
import { useCustomerDetailUIStore } from "../../store/use-customer-detail-ui-store";
import { CustomerEditForm } from "../forms/CustomerEditForm";

interface CustomerEditDialogProps {
  customer: CustomerDetailFragment;
}

export function CustomerEditDialog({ customer }: CustomerEditDialogProps) {
  const { t } = useTranslation("customers");
  const { isEditDialogOpen, closeEditDialog } = useCustomerDetailUIStore();
  const { form, handleSubmit, isLoading, error } = useCustomerEdit({
    customer,
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (isEditDialogOpen) {
      form.reset();
    }
  }, [isEditDialogOpen, form]);

  const onSubmit = async (data) => {
    const success = await handleSubmit(data);
    if (success) {
      closeEditDialog();
    }
  };

  return (
    <Dialog open={isEditDialogOpen} onOpenChange={closeEditDialog}>
      <DialogContent className="flex flex-col sm:max-w-[600px] max-h-[85vh]">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            {t("dialogs.editCustomer")}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <ScrollArea className="flex-1 overflow-y-auto">
            <div className="p-1">
              <CustomerEditForm form={form} isLoading={isLoading} error={error} />
            </div>
          </ScrollArea>

          {/* Fixed Footer */}
          <div className="flex-shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 p-4 border-t bg-background">
            <Button type="button" variant="outline" onClick={closeEditDialog} disabled={isLoading}>
              {t("dialogs.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t("dialogs.saving") : t("dialogs.saveChanges")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
